import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import CleaningServicesRoundedIcon from "@mui/icons-material/CleaningServicesRounded";
import { PageHeader } from "../../components/cards/PageHeader";
import { AppColors } from "../../constants/AppColors";
import { CleaningTaskResponse } from "../../models/ResponseModels/CleaningTaskResponse";
import { AuthUserResponse } from "../../models/ResponseModels/AuthUserResponse";
import { formatShortDisplayDate } from "../../utils/helperFunctions/dateFormatter";
import { useCleaningTasksScreen } from "./hooks/useCleaningTasksScreen";

type TaskSectionProps = {
  title: string;
  emptyText: string;
  tasks: CleaningTaskResponse[];
  showActions?: boolean;
  canAssignTasks: boolean;
  assignableUsers: AuthUserResponse[];
  onAssign: (
    task: CleaningTaskResponse,
    assignedToUserId: number | null,
  ) => void;
  onStart: (task: CleaningTaskResponse) => void;
  onDone: (task: CleaningTaskResponse) => void;
  t: (key: string) => string;
};

const getStatusColor = (
  status: CleaningTaskResponse["status"],
): "default" | "success" | "warning" | "error" | "info" => {
  if (status === "done") {
    return "success";
  }

  if (status === "in_progress") {
    return "info";
  }

  if (status === "cancelled") {
    return "error";
  }

  if (status === "pending") {
    return "warning";
  }

  return "default";
};

const formatTaskDateTime = (task: CleaningTaskResponse) => {
  const date = formatShortDisplayDate(task.scheduled_date);

  if (!task.scheduled_time) {
    return date;
  }

  return `${date} ${task.scheduled_time}`;
};

const TaskSection = ({
  title,
  emptyText,
  tasks,
  showActions = true,
  canAssignTasks,
  assignableUsers,
  onAssign,
  onStart,
  onDone,
  t,
}: TaskSectionProps) => {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 950, mb: 1.5 }}>
        {title}
      </Typography>

      {tasks.length === 0 ? (
        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            border: `1px solid ${AppColors.border}`,
          }}
        >
          <CardContent>
            <Stack spacing={1} sx={{ py: 3, alignItems: "center" }}>
              <CleaningServicesRoundedIcon
                color="disabled"
                sx={{ fontSize: 40 }}
              />

              <Typography color="text.secondary">{emptyText}</Typography>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "repeat(2, 1fr)" },
            gap: 2,
          }}
        >
          {tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              showActions={showActions}
              canAssignTasks={canAssignTasks}
              assignableUsers={assignableUsers}
              onAssign={onAssign}
              onStart={onStart}
              onDone={onDone}
              t={t}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

const TaskCard = ({
  task,
  showActions,
  canAssignTasks,
  assignableUsers,
  onAssign,
  onStart,
  onDone,
  t,
}: {
  task: CleaningTaskResponse;
  showActions: boolean;
  canAssignTasks: boolean;
  assignableUsers: AuthUserResponse[];
  onAssign: (
    task: CleaningTaskResponse,
    assignedToUserId: number | null,
  ) => void;
  onStart: (task: CleaningTaskResponse) => void;
  onDone: (task: CleaningTaskResponse) => void;
  t: (key: string) => string;
}) => {
  const isPending = task.status === "pending";
  const isInProgress = task.status === "in_progress";

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 4,
        border: `1px solid ${AppColors.border}`,
        bgcolor: AppColors.card,
        boxShadow: AppColors.shadowCard,
      }}
    >
      <CardContent
        sx={{
          p: { xs: 2, sm: 2.5 },
          "&:last-child": {
            pb: { xs: 2, sm: 2.5 },
          },
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction="row"
            spacing={2}
            sx={{
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 950 }}>
                {task.unit?.name || t("booking.unit")}
              </Typography>

              <Typography color="text.secondary">
                {t("cleaning.scheduled")}: {formatTaskDateTime(task)}
              </Typography>

              <Typography color="text.secondary">
                {t("cleaning.assignedTo")}:{" "}
                {task.assigned_to?.name || t("cleaning.unassigned")}
              </Typography>

              {task.completed_at ? (
                <Typography color="text.secondary">
                  {t("cleaning.cleanedAt")}:{" "}
                  {formatShortDisplayDate(task.completed_at)}
                </Typography>
              ) : null}
            </Box>

            <Chip
              size="small"
              color={getStatusColor(task.status)}
              label={t(`cleaning.status.${task.status}`)}
              sx={{ fontWeight: 900 }}
            />
          </Stack>

          {task.notes ? <Alert severity="info">{task.notes}</Alert> : null}

          {showActions && canAssignTasks ? (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 3,
                bgcolor: AppColors.surfaceSoft,
                border: `1px solid ${AppColors.border}`,
              }}
            >
              <Typography sx={{ fontWeight: 900, mb: 1 }}>
                {t("cleaners.assignCleaner")}
              </Typography>

              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                <Button
                  size="small"
                  variant={!task.assigned_to_user_id ? "contained" : "outlined"}
                  onClick={() => onAssign(task, null)}
                >
                  {t("cleaning.unassigned")}
                </Button>

                {assignableUsers.map((cleaner) => (
                  <Button
                    key={cleaner.id}
                    size="small"
                    variant={
                      task.assigned_to_user_id === cleaner.id
                        ? "contained"
                        : "outlined"
                    }
                    onClick={() => onAssign(task, cleaner.id)}
                  >
                    {cleaner.first_name}
                  </Button>
                ))}
              </Stack>
            </Box>
          ) : null}

          {showActions && task.status !== "done" ? (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              {isPending ? (
                <Button variant="outlined" onClick={() => onStart(task)}>
                  {t("cleaning.start")}
                </Button>
              ) : null}

              {isPending || isInProgress ? (
                <Button variant="contained" onClick={() => onDone(task)}>
                  {t("cleaning.markCleaned")}
                </Button>
              ) : null}
            </Stack>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
};

const CleaningTasksScreen = () => {
  const vm = useCleaningTasksScreen();

  return (
    <Box>
      <PageHeader
        title={vm.t("tabs.cleaningTasks")}
        description={vm.t("cleaning.description")}
        action={
          <Button
            variant="outlined"
            startIcon={<RefreshRoundedIcon />}
            onClick={vm.refreshTasks}
            disabled={vm.loading}
          >
            {vm.t("common.refresh")}
          </Button>
        }
      />

      {vm.error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {vm.error}
        </Alert>
      ) : null}

      {vm.loading &&
      vm.todayTasks.length === 0 &&
      vm.upcomingTasks.length === 0 &&
      vm.doneTasks.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TaskSection
            title={vm.t("cleaning.today")}
            emptyText={vm.t("cleaning.noTasksToday")}
            tasks={vm.todayTasks}
            canAssignTasks={vm.canAssignTasks}
            assignableUsers={vm.assignableUsers}
            onAssign={vm.assignTask}
            onStart={vm.markInProgress}
            onDone={vm.markDone}
            t={vm.t}
          />

          <TaskSection
            title={vm.t("cleaning.upcoming")}
            emptyText={vm.t("cleaning.noUpcomingTasks")}
            tasks={vm.upcomingTasks}
            canAssignTasks={vm.canAssignTasks}
            assignableUsers={vm.assignableUsers}
            onAssign={vm.assignTask}
            onStart={vm.markInProgress}
            onDone={vm.markDone}
            t={vm.t}
          />

          <TaskSection
            title={vm.t("cleaning.done")}
            emptyText={vm.t("cleaning.noDoneTasks")}
            tasks={vm.doneTasks}
            showActions={false}
            canAssignTasks={vm.canAssignTasks}
            assignableUsers={vm.assignableUsers}
            onAssign={vm.assignTask}
            onStart={vm.markInProgress}
            onDone={vm.markDone}
            t={vm.t}
          />
        </>
      )}
    </Box>
  );
};

export default CleaningTasksScreen;
